import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import swal from 'sweetalert2';
import Loading from '../Loading';
import { getExam, editAnswersExam } from '../../actions/examsActions';
import usePreventDoubleSubmit, { getSwalSubmitGuardOptions } from '../../customHooks/usePreventDoubleSubmit';

const styles = {
  main: {
    display: 'flex',
    minHeight: '70vh',
    flexFlow: 'column nowrap',
    justifyContent: 'space-between',
  },
  section: {
    padding: '20px',
  },
};

const MODES = {
  upsert: {
    label: 'Añadir / actualizar',
    swalType: 'question',
    steps: [
      'Creará respuestas para estudiantes que aún no tienen respuesta.',
      'Si el estudiante no existe, se creará.',
      'Actualizará el texto de respuestas existentes.',
      'Omitirá respuestas que ya tienen asignaciones previas.',
      'No eliminará respuestas existentes.',
    ],
  },
  replace: {
    label: 'Importar desde cero',
    swalType: 'warning',
    steps: [
      'Eliminará todas las respuestas que no tengan asignaciones previas.',
      'Si el estudiante no existe, se creará.',
      'Creará las respuestas del archivo.',
    ],
  },
};

const confirmImportMessage = (mode) => {
  const { label, steps } = MODES[mode];
  return `
    <div style="font-size:15px;text-align:left">
      <p><strong>Modo:</strong> ${label}</p>
      <p style="margin-top:12px"><strong>Esta acción:</strong></p>
      <ul style="padding-left:20px">
        ${steps.map((s) => `<li>${s}</li>`).join('')}
      </ul>
    </div>
  `;
};

const buildImportSummary = (data) => {
  const parts = [];
  if (data.deleted > 0) parts.push(`${data.deleted} eliminadas`);
  if (data.created > 0) parts.push(`${data.created} creadas`);
  if (data.updated > 0) parts.push(`${data.updated} actualizadas`);
  if (data.skipped > 0) parts.push(`${data.skipped} omitidas`);
  return parts.length > 0 ? parts.join(', ') : 'No se procesaron respuestas';
};

const ImportTextAnswers = (props) => {
  const examId = props.match.params.id;
  const { exam } = props;
  const excelInputRef = useRef(null);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('upsert');
  const { isSubmitting, guardSubmit, startSubmit, wrapCallbacks } = usePreventDoubleSubmit({ loadingText: 'Importando...' });

  useEffect(() => {
    props.getExam(examId);
  }, [examId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!guardSubmit()) return;

    const excelFile = excelInputRef.current?.files[0];

    if (!excelFile) {
      swal('Un momento', 'Debe seleccionar el archivo Excel', 'warning');
      return;
    }
    if (excelFile.name.split('.').pop() !== 'xlsx') {
      swal('Un momento', 'El archivo de respuestas debe tener formato .xlsx', 'warning');
      return;
    }

    swal({
      title: 'Confirmar importación',
      html: confirmImportMessage(mode),
      type: MODES[mode].swalType,
      width: '450px',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      ...getSwalSubmitGuardOptions('Importando...'),
    }).then((confirm) => {
      if (!confirm.value || !guardSubmit()) return;

      const formData = new FormData();
      formData.append('answers_list', excelFile);
      formData.append('mode', mode);

      startSubmit();
      setResult(null);

      const { successCallback, errorCallback } = wrapCallbacks(
        (data) => {
          setResult(data);
          const errCount = data.errors ? data.errors.length : 0;
          if (errCount === 0) {
            swal('Importación completada', buildImportSummary(data), 'success');
          } else {
            swal(
              'Importación con errores',
              `${buildImportSummary(data)}. Hubo ${errCount} errores.`,
              'warning'
            );
          }
        },
        (error) => {
          const responseData = error?.response?.data;
          const validationErrors = responseData?.errors;

          if (validationErrors && validationErrors.length > 0) {
            setResult({ created: 0, updated: 0, deleted: 0, skipped: 0, errors: validationErrors });
            swal({
              type: 'error',
              title: 'Error al importar respuestas',
              text: `Se encontraron ${validationErrors.length} errores.`,
            });
            return;
          }

          const msg = responseData?.error || 'Error al importar las respuestas';
          swal('Error', msg, 'error');
        }
      );

      props.editAnswersExam(examId, formData, successCallback, errorCallback);
    });
  };

  if (!exam?.tests) return <Loading />;

  return (
    <main style={styles.main}>
      <form onSubmit={handleSubmit}>
        <section style={styles.section}>
          <h2>Importar respuestas de texto</h2>
          <p>
            Suba un archivo Excel (.xlsx) con las columnas{' '}
            <strong>Email</strong>, <strong>Respuesta</strong> y{' '}
            <strong>Test_id</strong>.
          </p>
          <p>
            La columna <strong>Test_id</strong> debe contener el id de la pregunta asociada al examen.
            La columna <strong>Respuesta</strong> debe contener el texto de la respuesta del estudiante.
          </p>

          <p>Preguntas de este examen y sus <strong>Test_id</strong>:</p>
          <table className="table">
            <thead>
              <tr>
                <td><strong>Pregunta</strong></td>
                <td><strong>Test_id</strong></td>
              </tr>
            </thead>
            <tbody>
              {exam.tests.map((test) => (
                <tr key={test.id}>
                  <td>{test.title}</td>
                  <td>{test.id}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p>Ejemplo del Excel:</p>
          <table className="table">
            <thead>
              <tr>
                <td><strong>Email</strong></td>
                <td><strong>Respuesta</strong></td>
                <td><strong>Test_id</strong></td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>usuario1@correo.edu.co</td>
                <td>El texto de la respuesta del estudiante...</td>
                <td>39</td>
              </tr>
              <tr>
                <td>usuario2@correo.edu.co</td>
                <td>Otra respuesta de ejemplo</td>
                <td>39</td>
              </tr>
            </tbody>
          </table>

          <div className="row" style={{ marginBottom: '16px' }}>
            <div className="col-md-4 col-xs-12">
              <label>Tipo de importación</label>
              <select
                className="form-control"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={isSubmitting}
              >
                {Object.entries(MODES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 col-xs-12">
              <label>Excel de respuestas (.xlsx)</label>
              <br />
              <input ref={excelInputRef} type="file" accept=".xlsx" required disabled={isSubmitting} />
            </div>
          </div>

          {result && (
            <div style={{ marginTop: '16px' }}>
              {result.errors && result.errors.length > 0 ? (
                <div>
                  <p className="alert alert-danger">
                    <strong>Error al importar respuestas:</strong>
                  </p>
                  <ul className="text-danger">
                    {result.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="alert alert-success">
                  <p><strong>Resumen:</strong> {buildImportSummary(result)}</p>
                </div>
              )}
            </div>
          )}
        </section>

        <section
          style={{
            display: 'flex',
            alignItems: 'center',
            flexFlow: 'row nowrap',
            justifyContent: 'space-between',
            margin: '2rem',
          }}
        >
          <button
            type="button"
            onClick={() => props.history.push(`/exams/${examId}/editParticipants`)}
            className="btn btn-danger"
          >
            Volver
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Importando...' : 'Importar respuestas'}
          </button>
        </section>
      </form>
    </main>
  );
};

function mapStateToProps(state, ownProps) {
  return {
    exam: state.exams[ownProps.match.params.id],
  };
}

export default connect(mapStateToProps, { getExam, editAnswersExam })(ImportTextAnswers);
